import { computed, type ComputedRef, ref, type Ref } from 'vue';
import type ContactData from '@/chat/types/ContactData';
import ChatSession from '@/chat/types/ChatSession';
import ChatClient from '@/chat/ChatClient';
import type Interaction from '@/chat/types/Interaction';
import { InteractionType } from '@keyvue/one-api-client';
import type { User } from '@/chat/types/User';
import type StartChatResponse from '@/chat/types/StartChatResponse';

export default class Chat {
    private static instance: Chat;

    private isPreview?: boolean;

    private numberOfFails = 0;

    private assistentId?: string;

    private greeting: string = '';

    private contactData: ContactData = {
        name: null,
        email: null,
        phoneNumber: null,
        postalCode: null,
        houseNumber: null,
    };

    private messageQueue: string[] = [];

    public loading = ref(false);

    public loadingAssistentMessage = ref(false);

    public lostConnection = ref(false);

    public quickOptionsVisible = ref(true);

    public isClosed = ref(false);

    public isStarting = ref<boolean>(false);

    public startingPromise: Promise<StartChatResponse> | null = null;

    private constructor(
        private client: ChatClient,
        private session: Ref<ChatSession | null> = ref(null),
        private interactions: Ref<Record<string, Interaction>> = ref({}),
    ) {
        this.session.value = ChatSession.fromSession();
    }

    public static getInstance(): Chat {
        if (!Chat.instance) {
            Chat.instance = new Chat(
                new ChatClient(),
            );
        }

        return Chat.instance;
    }

    public async initialize(
        assistentId: string,
        greeting: string,
        isPreview: boolean,
    ): Promise<void> {
        if (this.assistentId !== undefined) {
            throw new Error('Chat already initialized');
        }

        this.assistentId = assistentId;
        this.greeting = greeting;
        this.isPreview = isPreview;

        if (window.sessionStorage.getItem('invesl-quickOptionsVisible')) {
            this.quickOptionsVisible.value = window.sessionStorage.getItem('invesl-quickOptionsVisible') === 'true';
        } else {
            window.sessionStorage.setItem('invesl-quickOptionsVisible', 'true');
        }

        this.addPreviewInteraction();
        this.addGreetingInteraction();

        if (this.isPreview) {
            await this.agreeToPrivacyTerms();
        }

        if (this.session.value === null) {
            return;
        }

        await this.loadChat();
        await this.startListening();
    }

    public async restartChat(): Promise<void> {
        if (!this.session) {
            return;
        }

        // @todo: zouden we netter moeten doen
        window.sessionStorage.clear();
        window.location.reload();
    }

    private async start(): Promise<void> {
        if (this.isStarting.value) {
            return;
        }

        if (this.assistentId === undefined) {
            return;
        }

        this.isStarting.value = true;
        this.startingPromise = this.client.startChat(this.assistentId, this.contactData);
        const response = await this.startingPromise;

        this.session.value = ChatSession.startSession(
            this.assistentId,
            response.chatId,
            response.hub,
            response.topic,
            response.token,
        );
        this.isStarting.value = false;
        this.startingPromise = null;

        await this.startListening();
    }

    private async restart(): Promise<void> {
        if (!this.session.value) {
            throw new Error('No chat to restart');
        }

        const response = await this.client.generateChatToken(this.session?.value.chatId);
        this.session.value.changeToken(response.hub, response.topic, response.token);

        await this.startListening();

        this.lostConnection.value = false;
    }

    private async startListening(): Promise<void> {
        if (!this.session.value) {
            throw new Error('Chat session not started');
        }

        const onMessage = async (message: any) => {
            if (!this.session.value) {
                throw new Error('Chat session not started');
            }

            if (message.event === 'chat.requires-handover') {
                const { handoverExpiresAt } = JSON.parse(message.data);
                this.session.value.handoverExpiresAt = handoverExpiresAt;
            }

            if (message.event === 'chat.closed') {
                this.isClosed.value = true;
                return;
            }

            if (message.event === 'chat.handover-accepted') {
                const { handoverAcceptedBy } = JSON.parse(message.data);
                this.session.value.handoverAcceptedBy = handoverAcceptedBy;
                this.session.value.handoverExpiresAt = null;
            }

            if (message.event === 'interaction.created') {
                const interaction = JSON.parse(message.data) as Interaction;
                this.interactions.value[interaction.id] = interaction;

                if (interaction.origin === 'assistent') {
                    this.loadingAssistentMessage.value = false;
                }
            }

            if (message.event === 'interaction.content_changed') {
                const interaction = JSON.parse(message.data) as Interaction;
                const lines = interaction.content.split('\n');

                this.interactions.value[interaction.id].content = lines.map((line) => {
                    if (line.startsWith('**')) {
                        return line.endsWith('**') ? line : `${line}**`;
                    }

                    return line;
                }).join('\n');
            }

            if (message.event === 'interaction.completed') {
                const interaction = JSON.parse(message.data) as Interaction;

                this.interactions.value[interaction.id].content = interaction.content;
                this.interactions.value[interaction.id].completed = interaction.completed;

                if (interaction.origin === 'assistent') {
                    this.loading.value = false;
                }

                if (interaction.origin === 'user' && this.session.value.handoverAcceptedBy === null) {
                    this.loadingAssistentMessage.value = true;
                }
            }
        };

        const onClose = async () => {
            this.numberOfFails += 1;

            if (this.numberOfFails > 1) {
                this.lostConnection.value = true;
                return;
            }

            await this.restart();
        };

        const onError = async (error: any) => {
            this.numberOfFails += 1;

            if (this.numberOfFails > 1) {
                this.lostConnection.value = true;
                throw error;
            }

            await this.restart();
        };

        /** We don't await here since this is an actual async process */
        this.client.listenForMessages(
            this.session.value,
            onMessage,
            onClose,
            onError,
        );
    }

    public async sendMessage(message: string): Promise<void> {
        if (this.startingPromise !== null) {
            await this.startingPromise;
        }

        if (this.session.value === null) {
            await this.start();
        }

        if (this.session.value === null) {
            throw new Error('Session not started');
        }

        /** always hide quickoptions after a message */
        this.hideQuickOptions();

        /** if not agreed to terms we save the messages for after agreeing */
        if (!this.session.value.agreedToPrivacyTerms) {
            this.messageQueue.push(message);
            return;
        }

        // show loading when sending a message, unless we are in a handover
        this.loading.value = !(
            this.session.value.handoverExpiresAt !== null
            || this.session.value.handoverAcceptedBy !== null
        );

        await this.client.sendMessage(
            this.session.value.assistentId,
            this.session.value.chatId,
            message,
        );
    }

    private async processMessageQueue() {
        const promises: Promise<void>[] = [];

        this.messageQueue.forEach((message) => {
            promises.push(this.sendMessage(message));
        });

        this.messageQueue = [];

        await Promise.all(promises);
    }

    public async agreeToPrivacyTerms(): Promise<void> {
        if (this.startingPromise !== null) {
            await this.startingPromise;
        }

        if (this.session.value === null) {
            await this.start();
        }

        if (this.session.value === null) {
            throw new Error('Session not started');
        }

        this.session.value.agreeToPrivacyTerms();

        /**
         * When agreed to privacy terms we processed the potentially saved messages.
         * This should almost always be 1 message
         * */
        await this.processMessageQueue();
    }

    public changeContactData(data: ContactData) {
        this.contactData = data;
    }

    private async loadChat(): Promise<void> {
        if (this.session.value === null) {
            return;
        }

        this.loading.value = true;

        const chat = await this.client.loadChat(this.session.value.chatId);

        // update session based on loaded chat
        this.session.value.handoverAcceptedBy = chat.handoverAcceptedBy;
        this.session.value.handoverExpiresAt = chat.handoverExpiresAt;

        chat.interactions.forEach((interaction) => {
            this.interactions.value[interaction.id] = interaction;

            if (interaction.type === 'chat-closed') {
                this.isClosed.value = true;
            }
        });

        this.loading.value = false;
    }

    private addGreetingInteraction() {
        if (this.greeting === '') {
            return;
        }

        this.interactions.value['2'] = {
            id: '2',
            type: 'message',
            content: this.greeting,
            origin: 'assistent',
            url: '',
            sentBy: undefined,
            completed: true,
            handoverAcceptedBy: null,
            createdAt: (new Date()).toISOString(),
        };
    }

    private addPreviewInteraction() {
        if (!this.isPreview) {
            return;
        }

        this.interactions.value['1'] = {
            id: '1',
            type: 'preview',
            content: '',
            origin: 'system',
            url: '',
            sentBy: undefined,
            completed: true,
            handoverAcceptedBy: null,
            createdAt: (new Date()).toISOString(),
        };
    }

    public get isStarted(): ComputedRef<boolean> {
        return computed(() => (
            this.session.value !== null
        ));
    }

    public get showHandoverOverlay(): ComputedRef<boolean> {
        return computed(() => {
            if (this.session.value === null) {
                return false;
            }

            return this.session.value.handoverExpiresAt !== null;
        });
    }

    public get handoverSecondsLeft() {
        if (this.session.value === null || this.session.value.handoverExpiresAt === null) {
            return 0;
        }

        const expireDate = new Date(this.session.value.handoverExpiresAt);
        if (expireDate === null || expireDate === undefined) {
            return 0;
        }

        const currentDate = new Date();
        if (expireDate <= currentDate) {
            return 0;
        }

        return Math.floor((expireDate.getTime() - currentDate.getTime()) / 1000);
    }

    public hideQuickOptions() {
        window.sessionStorage.setItem('invesl-quickOptionsVisible', 'false');
        this.quickOptionsVisible.value = false;
    }

    public get isQuickOptionHidden() {
        return !this.quickOptionsVisible.value;
    }

    public get handoverAcceptedBy(): ComputedRef<User | null> {
        return computed(() => {
            if (this.session.value === null) {
                return null;
            }

            return this.session.value?.handoverAcceptedBy;
        });
    }

    public async cancelHandover(): Promise<void> {
        if (this.session.value === null) {
            throw new Error('Session not started');
        }

        await this.client.cancelHandover(this.session.value);
        this.session.value.handoverExpiresAt = null;
    }

    public async contactMeLater(form: Record<string, any>): Promise<void> {
        if (this.session.value === null) {
            throw new Error('Session not started');
        }

        await this.client.contactMeLater(this.session.value, form);
        this.session.value.handoverExpiresAt = null;
    }

    public get isInitialized(): boolean {
        return this.assistentId !== undefined;
    }

    public get hasUserMessage(): ComputedRef<boolean> {
        return computed(() => (
            Object.keys(this.interactions.value).length > 1
        ));
    }

    public get numberOfDefaultMessages() {
        let number = 0;

        if (this.greeting !== '') {
            number += 1;
        }

        if (this.isPreview) {
            number += 1;
        }

        return number;
    }

    public getInteractions(): ComputedRef<Interaction[]> {
        return computed(() => {
            const excludedTypes = [
                InteractionType.FUNCTION.value,
                InteractionType.HANDOVER_REFERRED.value,
            ];

            /** we get all interactions in a chat but we dont show functions here */
            const localInteractions = Object.values(
                this.interactions.value,
            ).filter(
                (interaction) => !excludedTypes.includes(interaction.type),
            );

            /** we dont show anything if you dont agree */
            if (!this.session.value?.agreedToPrivacyTerms) {
                return localInteractions.slice(0, this.numberOfDefaultMessages + 1);
            }

            return localInteractions;
        });
    }
}
